import { useState, useMemo, useCallback, useEffect } from "react";
import { List, useTable } from "@refinedev/antd";
import { Button, Input, Select, Space, Table, Tag, Tooltip, Popconfirm, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { DeleteOutlined, EditOutlined, PlusOutlined, WarningOutlined, SearchOutlined, FilterOutlined } from "@ant-design/icons";
import { useDelete, getDefaultFilter, useGo } from "@refinedev/core";
import { resolveApiBase } from "@/providers/api-base";

const { Text } = Typography;

const API_URL = resolveApiBase("http://localhost:8085");

type StopWord = {
  id: string;
  word: string;
  category: string;
  action: string;
  is_active: boolean;
  description?: string;
  created_at: string;
  updated_at: string;
};

interface StopWordCategory {
  id: string;
  label: string;
  color: string;
  action_type: string;
  description?: string;
}

export default function StopWordsList() {
  const go = useGo();

  // Получаем категории из API
  const [categories, setCategories] = useState<StopWordCategory[]>([]);

  useEffect(() => {
    fetch(`${API_URL}/api/v1/stop-word-categories`)
      .then(res => res.json())
      .then(data => setCategories(data.items || []))
      .catch(() => {});
  }, []);

  // Создаем categoryMap из API данных
  const categoryMap = useMemo(() => {
    const map: Record<string, { label: string; color: string }> = {};
    categories.forEach((cat) => {
      map[cat.id] = { label: cat.label, color: cat.color };
    });
    return map;
  }, [categories]);

  const {
    tableProps,
    filters,
    setFilters,
    tableQuery,
  } = useTable<StopWord>({
    resource: "stop-words",
    pagination: { mode: "server", pageSize: 20 },
    sorters: { initial: [{ field: "created_at", order: "desc" }] },
    filters: {
      initial: [
        { field: "word", operator: "contains", value: undefined },
        { field: "category", operator: "eq", value: undefined },
        { field: "is_active", operator: "eq", value: undefined },
      ],
    },
    syncWithLocation: true,
  });

  const { mutate: deleteStopWord } = useDelete();

  const [searchDraft, setSearchDraft] = useState("");
  const [categoryDraft, setCategoryDraft] = useState<string | undefined>();
  const [statusDraft, setStatusDraft] = useState<boolean | undefined>();

  const appliedSearchValue = useMemo(() => {
    const f = getDefaultFilter("word", filters);
    return typeof f?.value === "string" ? f.value : "";
  }, [filters]);

  const appliedCategoryValue = useMemo(() => {
    const f = getDefaultFilter("category", filters);
    return (f?.value as string | undefined) ?? undefined;
  }, [filters]);

  const appliedStatusValue = useMemo(() => {
    const f = getDefaultFilter("is_active", filters);
    return (f?.value as boolean | undefined) ?? undefined;
  }, [filters]);

  // Синхронизация draft-состояний с примененными фильтрами
  useEffect(() => {
    setSearchDraft(appliedSearchValue);
  }, [appliedSearchValue]);

  useEffect(() => {
    setCategoryDraft(appliedCategoryValue);
  }, [appliedCategoryValue]);

  useEffect(() => {
    setStatusDraft(appliedStatusValue);
  }, [appliedStatusValue]);

  const updateFilters = useCallback(
    (search: string, category?: string, status?: boolean) => {
      const trimmed = search.trim();
      setFilters(
        [
          { field: "word", operator: "contains", value: trimmed || undefined },
          { field: "category", operator: "eq", value: category || undefined },
          { field: "is_active", operator: "eq", value: status !== undefined ? status : undefined },
        ],
        "replace"
      );
    },
    [setFilters]
  );


  const handleDelete = useCallback(
    (id: string, word: string) => {
      deleteStopWord(
        {
          resource: "stop-words",
          id,
          successNotification: () => ({
            message: `Стоп-слово "${word}" удалено`,
            description: "",
            type: "success",
          }),
        },
        {
          onSuccess: () => {
            tableQuery.refetch();
          },
        }
      );
    },
    [deleteStopWord, tableQuery]
  );

  const renderCategory = useCallback((category: string) => {
    const config = categoryMap[category] || { label: category, color: "default" };
    return <Tag color={config.color}>{config.label}</Tag>;
  }, [categoryMap]);


  const columns: ColumnsType<StopWord> = [
    {
      title: "Дата создания",
      dataIndex: "created_at",
      width: 120,
      sorter: true,
      defaultSortOrder: "descend",
      render: (date) => dayjs(date).format("YYYY-MM-DD"),
      align: "center"
    },
    {
      title: "Слово/Фраза",
      dataIndex: "word",
      width: 200,
      filteredValue: appliedSearchValue ? [appliedSearchValue] : null,
      filterIcon: <SearchOutlined style={{ color: appliedSearchValue ? "#1677ff" : undefined }} />,
      filterDropdown: ({ close }) => (
        <div style={{ padding: 8, width: 260 }} onKeyDown={(e) => e.stopPropagation()}>
          <Input
            allowClear
            placeholder="Поиск по слову/фразе"
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            onPressEnter={() => {
              updateFilters(searchDraft, categoryDraft, statusDraft);
              close();
            }}
            style={{ marginBottom: 8 }}
          />
          <Space size={8}>
            <Button type="primary" size="small" onClick={() => {
              updateFilters(searchDraft, categoryDraft, statusDraft);
              close();
            }}>
              Применить
            </Button>
            <Button size="small" onClick={() => {
              setSearchDraft("");
              updateFilters("", categoryDraft, statusDraft);
              close();
            }}>
              Сбросить
            </Button>
          </Space>
        </div>
      ),
      render: (word, record) => (
        <Space>
          <WarningOutlined style={{ color: record.is_active ? "#faad14" : "#d9d9d9" }} />
          <Text strong={record.is_active} style={{ color: record.is_active ? undefined : "#999" }}>
            {word}
          </Text>
        </Space>
      ),
    },
    {
      title: "Категория",
      dataIndex: "category",
      width: 200,
      filteredValue: appliedCategoryValue ? [appliedCategoryValue] : null,
      filterIcon: <FilterOutlined style={{ color: appliedCategoryValue ? "#1677ff" : undefined }} />,
      filterDropdown: ({ close }) => (
        <div style={{ padding: 8, width: 200 }} onKeyDown={(e) => e.stopPropagation()}>
          <Select
            allowClear
            placeholder="Выберите категорию"
            style={{ width: '100%', marginBottom: 8 }}
            value={categoryDraft}
            onChange={(value) => setCategoryDraft(value)}
            showSearch
            optionFilterProp="label"
            options={categories.map((cat) => ({
              value: cat.id,
              label: cat.label,
            }))}
          />
          <Space size={8}>
            <Button type="primary" size="small" onClick={() => {
              updateFilters(searchDraft, categoryDraft, statusDraft);
              close();
            }}>
              Применить
            </Button>
            <Button size="small" onClick={() => {
              setCategoryDraft(undefined);
              updateFilters(searchDraft, undefined, statusDraft);
              close();
            }}>
              Сбросить
            </Button>
          </Space>
        </div>
      ),
      render: renderCategory,
    },
    {
      title: "Действие",
      dataIndex: "action",
      width: 120,
      align: "center",
      render: (action: string) =>
        action === "trigger" ? (
          <Tag color="processing">Триггер</Tag>
        ) : (
          <Tag color="warning">Блокировка</Tag>
        ),
    },
    {
      title: "Статус",
      dataIndex: "is_active",
      width: 100,
      align: "center",
      filteredValue: appliedStatusValue !== undefined ? [appliedStatusValue] : null,
      filterIcon: <FilterOutlined style={{ color: appliedStatusValue !== undefined ? "#1677ff" : undefined }} />,
      filterDropdown: ({ close }) => (
        <div style={{ padding: 8, width: 180 }} onKeyDown={(e) => e.stopPropagation()}>
          <Select
            allowClear
            placeholder="Статус"
            style={{ width: '100%', marginBottom: 8 }}
            value={statusDraft}
            onChange={(value) => setStatusDraft(value)}
            options={[
              { value: true, label: "Активно" },
              { value: false, label: "Неактивно" },
            ]}
          />
          <Space size={8}>
            <Button type="primary" size="small" onClick={() => {
              updateFilters(searchDraft, categoryDraft, statusDraft);
              close();
            }}>
              Применить
            </Button>
            <Button size="small" onClick={() => {
              setStatusDraft(undefined);
              updateFilters(searchDraft, categoryDraft, undefined);
              close();
            }}>
              Сбросить
            </Button>
          </Space>
        </div>
      ),
      render: (active: boolean) =>
        active ? (
          <Tag color="success">Активно</Tag>
        ) : (
          <Tag color="default">Неактивно</Tag>
        ),
    },
    {
      title: "Описание",
      dataIndex: "description",
      ellipsis: {
        showTitle: false,
      },
      render: (desc) => (
        <Tooltip placement="topLeft" title={desc}>
          <Text ellipsis>{desc}</Text>
        </Tooltip>
      ),
    },
    {
      title: "Действия",
      key: "actions",
      width: 100,
      fixed: "right",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Редактировать">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => {
                go({
                  to: { resource: "stop-words", action: "edit", id: record.id },
                  type: "push",
                });
              }}
              aria-label="Редактировать"
            />
          </Tooltip>
          <Popconfirm
            title="Удалить стоп-слово"
            description={`Вы уверены, что хотите удалить "${record.word}"?`}
            onConfirm={() => handleDelete(record.id, record.word)}
            okText="Удалить"
            cancelText="Отмена"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Удалить">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                aria-label="Удалить"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <List
        title="Стоп-слова"
        headerButtons={() => (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() =>
              go({
                to: {
                  resource: "stop-words",
                  action: "create",
                },
                type: "push",
              })
            }
          >
            Добавить стоп-слово
          </Button>
        )}
      >
        <Table
          {...tableProps}
          columns={columns}
          rowKey="id"
          bordered
          size="middle"
          scroll={{ x: 1200 }}
          pagination={{
            ...tableProps.pagination,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} из ${total} стоп-слов`,
          }}
        />
      </List>
    </>
  );
}
